import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';

import FormattedText from 'components/FormattedText/FormattedText';
import StyledRichText from 'components/StyledRichText';
import { Maybe } from '.tina/__generated__/types';
import type { ProjectItem } from 'blocks/ProjectsIndex/types';
import TinaVideo from 'components/TinaVideo/TinaVideo';
import TinaAsset from 'components/TinaAsset/TinaAsset';
import Icon from 'components/Icons/Icon';

interface IBackgroundAsset {
  $backgroundPosition: string;
  $autoWidth?: boolean;
}

interface MediaWrapper {
  href: string;
  $backgroundColor: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const OverlayButton = styled.button`
  border: 1px solid white;
  padding: 0.5rem 1rem;
  border-radius: 3rem;
  display: none;
  position: absolute;
`;

const StyledVideo = styled(TinaVideo)<IBackgroundAsset>`
  object-fit: cover;
  border-radius: 1rem;
  ${({ theme }) => theme.mq.md`
    border-radius: 1.5rem;
  `};
  width: 100%;
  height: 100%;
  object-position: ${({ $backgroundPosition }) => $backgroundPosition};
`;

const StyledAsset = styled(TinaAsset)<IBackgroundAsset>`
  overflow: auto;
  width: ${({ $autoWidth }) => ($autoWidth ? 'auto' : '100%')};
  height: 100%;
  object-fit: cover;
  object-position: ${({ $backgroundPosition }) => $backgroundPosition};
`;

const MediaWrapper = styled(Link)<MediaWrapper>`
  margin-bottom: 0.75rem;
  aspect-ratio: 4 / 3;
  border-radius: 1rem;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: grid;
  place-items: center;
  ${({ theme: { mq }, $backgroundColor }) => css`
    background-color: ${$backgroundColor};

    ${mq.md`
    border-radius: 1.5rem;`}

    ${mq.lg`
    margin-bottom: 1.5rem;
  `};
  `};
  &:hover ${OverlayButton} {
    display: block;
    background: transparent;
    color: white;
    z-index: 2;
  }

  &:hover {
    ::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0.5;
      background: #000;
      z-index: 1;
    }
  }
`;

const ContentWrapper = styled(Link)`
  z-index: 2;
  &:hover {
    text-decoration: none;
  }
`;

const TitleLine = styled.div`
  color: ${({ theme }) => theme.themeColors.fg};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const Title = styled(FormattedText)`
  margin-bottom: 0 !important;
  width: min-content;
  ${({ theme }) => theme.mq.lg`
    width: unset;
  `}
`;

const SubtitleWrapper = styled.div`
  width: 85%;
  ${({ theme }) => theme.mq.lg`
    width: 100%;
  `}
`;

const IconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.themeColors.primary};
  border-radius: 100%;
  height: 2.5rem;
  width: 2.5rem;

  svg {
    height: 1rem;
    width: 1.25rem;
  }

  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

interface IProjectItem {
  project: Maybe<ProjectItem>;
}

const ProjectIndexItem: React.FC<IProjectItem> = ({ project }: IProjectItem) => {
  return (
    <Container>
      <MediaWrapper
        href={project?.link || ''}
        $backgroundColor={project?.backgroundColor || 'transparent'}
      >
        <>
          <OverlayButton className="overlayButton">See the Case Study</OverlayButton>
          {project?.videoMeta?.url && (
            <StyledVideo
              src={project?.videoMeta?.url}
              $backgroundPosition={project?.backgroundPosition || 'center center'}
            />
          )}
          {project?.asset?.src && (
            <StyledAsset
              $autoWidth={project?.autoWidth || false}
              asset={project?.asset}
              $backgroundPosition={project?.backgroundPosition || 'center center'}
            />
          )}
        </>
      </MediaWrapper>
      <ContentWrapper href={project?.link || ''}>
        <>
          <TitleLine>
            <Title pFormat={'sansRegular52'}>{project?.title}</Title>
            <IconContainer>
              <Icon name="SkinnyArrow" />
            </IconContainer>
          </TitleLine>
          <SubtitleWrapper>
            <StyledRichText
              pFormat={project?.styledRichText?.pFormat}
              content={project?.styledRichText?.body}
            />
          </SubtitleWrapper>
        </>
      </ContentWrapper>
    </Container>
  );
};

export default ProjectIndexItem;
